/**
 * ========================================
 * USER JOURNEY PAGE COMPONENT
 * ========================================
 * 
 * This component displays a scroll-animated user journey timeline with 5 steps.
 * 
 * RESPONSIVE BREAKPOINTS:
 * - DESKTOP: ≥900px (md breakpoint and above)
 *   - Large curve: 750px width
 *   - Large images: 130px × 130px
 *   - Card width: 42% of container
 * 
 * - TABLET: 600px - 899px (sm to md breakpoint)
 *   - Medium curve: 85% of container width
 *   - Medium images: 90px × 90px
 *   - Card width: 85% of container
 * 
 * - MOBILE: <600px (below sm breakpoint)
 *   - Small curve: 75% of container width
 *   - Small images: 70px × 70px
 *   - Card width: 96% of container (nearly full width)
 * 
 * TO ADJUST IMAGE POSITIONS:
 * See comments in journeySteps array and JourneyStepCard component
 * 
 * TO ADJUST CURVE SIZE:
 * See comments in "Animated Curve Background" section
 */

import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router-dom";
import MeetingModal from "../../MeetingModal";

// Import images used in this component
import amicoAdvantage from "./assets/amico.png";
import newCurve from "./assets/newcurve.png";
// Note: Journey step images are imported in JourneyStepCards.jsx

// Import animated grid pattern component
import AnimatedGridPattern from "./AnimatedGridPattern";

// Import individual journey step card components (NO LOOPS - each card is separate)
import {
  JourneyStepCard1,
  JourneyStepCard2,
  JourneyStepCard3,
  JourneyStepCard4,
  JourneyStepCard5,
} from "./JourneyStepCards";

// ============ HERO SECTION ============
const HeroSection = ({ isSmallScreen, isMobile }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [titleWordsVisible, setTitleWordsVisible] = useState([false, false]);
  const [stepsVisible, setStepsVisible] = useState([false, false, false, false, false]);
  const [gradientPosition, setGradientPosition] = useState(0);

  // Opening animation for all devices (mobile, tablet, desktop) - triggers on page load
  useEffect(() => {
    // Small delay to ensure smooth animation
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Staggered title word animation
  useEffect(() => {
    if (isLoaded) {
      const timer1 = setTimeout(() => setTitleWordsVisible([true, false]), 200);
      const timer2 = setTimeout(() => setTitleWordsVisible([true, true]), 400);
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }
  }, [isLoaded]);

  // Staggered progress steps animation
  useEffect(() => {
    if (isLoaded) {
      const timers = [];
      for (let i = 0; i < 5; i++) {
        timers.push(
          setTimeout(() => {
            setStepsVisible((prev) => {
              const newState = [...prev];
              newState[i] = true;
              return newState;
            });
          }, 600 + i * 150)
        );
      }
      return () => timers.forEach((timer) => clearTimeout(timer));
    }
  }, [isLoaded]);

  // Animated gradient overlay
  useEffect(() => {
    const interval = setInterval(() => {
      setGradientPosition((prev) => (prev + 1) % 100);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  const steps = [
    { num: 1, label: "Reporting", active: true },
    { num: 2, label: isMobile ? "Retention" : "Retention boost", active: true },
    { num: 3, label: "Go Digital", active: true },
    { num: 4, label: "Counselling", active: false },
    { num: 5, label: isMobile ? "Marketing" : "Targeted marketing", active: false },
  ];

  return (
    <Box
      sx={{
        background: "linear-gradient(180deg, #022674 0%, #1E4DB7 50%, #3567D6 100%)",
        // Reduced padding for content-based height
        padding: { xs: "60px 12px 40px", sm: "75px 20px 45px", md: "90px 48px 50px" },
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
        // Content-based height - no minHeight constraint
        width: "100%",
        // Opening animation for all devices - fade in and slide up on page load
        opacity: isLoaded ? 1 : 0,
        transform: isLoaded ? "translateY(0)" : "translateY(30px)",
        transition: "opacity 0.8s ease-out, transform 0.8s ease-out",
      }}
    >
      {/* Animated gradient overlay */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(${gradientPosition * 3.6}deg, rgba(60, 208, 255, 0.1) 0%, transparent 50%, rgba(60, 208, 255, 0.05) 100%)`,
          pointerEvents: "none",
          transition: "background 0.5s ease-out",
        }}
      />

      {/* Animated Grid Pattern Background */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
          overflow: "hidden",
          maskImage: "radial-gradient(600px circle at center, white, transparent)",
          WebkitMaskImage: "radial-gradient(600px circle at center, white, transparent)",
          opacity: 1,
        }}
      >
        <AnimatedGridPattern
          numSquares={40}
          maxOpacity={0.25}
          duration={4}
          repeatDelay={0.5}
          width={40}
          height={40}
        />
      </Box>

      {/* Background decorative elements */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "radial-gradient(circle at 20% 80%, rgba(60, 208, 255, 0.12) 0%, transparent 50%)",
          pointerEvents: "none",
          zIndex: 1,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "radial-gradient(circle at 80% 20%, rgba(60, 208, 255, 0.08) 0%, transparent 40%)",
          pointerEvents: "none",
          zIndex: 1,
        }}
      />

      {/* Title with staggered animation */}
      <Box sx={{ position: "relative", zIndex: 2 }}>
        <Box
          sx={{
            display: "inline-block",
            marginRight: "8px",
            opacity: titleWordsVisible[0] ? 1 : 0,
            transform: titleWordsVisible[0] ? "translateY(0)" : "translateY(20px)",
            transition: "opacity 0.6s ease-out, transform 0.6s ease-out",
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "28px", sm: "40px", md: "52px", lg: "60px" },
              fontWeight: 800,
              color: "white",
              lineHeight: 1.1,
              fontFamily: "'Poppins', sans-serif",
              textShadow: "0 2px 20px rgba(0,0,0,0.2)",
            }}
          >
            Smart Health
          </Typography>
        </Box>
        <Box
          sx={{
            display: "inline-block",
            opacity: titleWordsVisible[1] ? 1 : 0,
            transform: titleWordsVisible[1] ? "translateY(0)" : "translateY(20px)",
            transition: "opacity 0.6s ease-out, transform 0.6s ease-out",
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "28px", sm: "40px", md: "52px", lg: "60px" },
              fontWeight: 800,
              color: "#3CD0FF",
              lineHeight: 1.1,
              marginBottom: { xs: "12px", md: "18px" },
              fontFamily: "'Poppins', sans-serif",
              textShadow: "0 2px 20px rgba(60, 208, 255, 0.3)",
            }}
          >
            Journey
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: { xs: "12px", sm: "13px", md: "15px" },
            color: "rgba(255, 255, 255, 0.9)",
            maxWidth: "580px",
            margin: "0 auto 22px",
            lineHeight: 1.6,
            padding: { xs: "0 6px", md: "0" },
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? "translateY(0)" : "translateY(10px)",
            transition: "opacity 0.8s ease-out 0.5s, transform 0.8s ease-out 0.5s",
          }}
        >
          Transform your healthcare experience with Niroggyan's intelligent diagnostics,
          personalized insights, and seamless patient-provider communication
        </Typography>

        {/* Progress Bar with enhanced animations */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            gap: { xs: "2px", sm: "6px", md: "10px" },
            padding: { xs: "10px 2px", md: "14px" },
            width: "100%",
            maxWidth: { xs: "100%", md: "800px" },
            margin: "0 auto",
          }}
        >
          {steps.map((step, index) => (
            <React.Fragment key={step.num}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: { xs: "3px", md: "6px" },
                  flex: { xs: "0 0 auto", md: 1 },
                  minWidth: { xs: "42px", sm: "55px", md: "auto" },
                  opacity: stepsVisible[index] ? 1 : 0,
                  transform: stepsVisible[index] ? "translateY(0)" : "translateY(10px)",
                  transition: `opacity 0.5s ease-out ${index * 0.15 + 0.6}s, transform 0.5s ease-out ${index * 0.15 + 0.6}s`,
                }}
              >
                <Box
                  sx={{
                    width: { xs: "24px", sm: "32px", md: "40px" },
                    height: { xs: "24px", sm: "32px", md: "40px" },
                    borderRadius: "50%",
                    background: step.active 
                      ? "linear-gradient(135deg, #3CD0FF 0%, #1E4DB7 50%, #022674 100%)"
                      : "rgba(255,255,255,0.25)",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: { xs: "10px", sm: "13px", md: "16px" },
                    fontFamily: "'Poppins', sans-serif",
                    boxShadow: step.active 
                      ? "0 4px 16px rgba(60, 208, 255, 0.4), 0 0 0 3px rgba(60, 208, 255, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.3)"
                      : "0 2px 8px rgba(0, 0, 0, 0.1)",
                    transition: "all 0.3s ease",
                    border: "none",
                    position: "relative",
                    "&::before": step.active ? {
                      content: '""',
                      position: "absolute",
                      inset: "-2px",
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, rgba(60, 208, 255, 0.3), rgba(30, 77, 183, 0.2))",
                      zIndex: -1,
                      opacity: 0.6,
                    } : {},
                  }}
                >
                  {step.num}
                </Box>
                 <Typography
                   sx={{
                     fontSize: { xs: "10px", sm: "12px", md: "14px" },
                     fontWeight: step.active ? 700 : 500,
                     color: "rgba(255,255,255,0.95)",
                     textAlign: "center",
                     lineHeight: 1.4,
                     maxWidth: { xs: "55px", sm: "75px", md: "110px" },
                     opacity: stepsVisible[index] ? 1 : 0,
                     transform: stepsVisible[index] ? "translateY(0)" : "translateY(10px)",
                     transition: `opacity 0.4s ease-out ${index * 0.15 + 0.9}s, transform 0.4s ease-out ${index * 0.15 + 0.9}s`,
                     textShadow: "0 1px 3px rgba(0, 0, 0, 0.3), 0 0 8px rgba(0, 0, 0, 0.2)",
                     letterSpacing: "0.3px",
                     whiteSpace: "normal",
                     wordBreak: "break-word",
                   }}
                 >
                   {step.label}
                 </Typography>
              </Box>
              {index < steps.length - 1 && (
                <Box
                  sx={{
                    position: "relative",
                    flex: "0 0 auto",
                    width: { xs: "8px", sm: "16px", md: "32px" },
                    height: "3px",
                    marginTop: { xs: "12px", sm: "16px", md: "20px" },
                    opacity: stepsVisible[index] && stepsVisible[index + 1] ? 1 : 0,
                    transition: "opacity 0.6s ease-out",
                    transitionDelay: `${index * 0.15 + 0.75}s`,
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: "50%",
                      left: 0,
                      width: "100%",
                      height: "2px",
                      backgroundColor: "transparent",
                      borderTop: "2px dashed rgba(255,255,255,0.6)",
                      transform: "translateY(-50%)",
                      borderRadius: "2px",
                      transition: "all 0.3s ease-out",
                    },
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

/**
 * ========================================
 * NOTE: Journey Step Cards are now in separate file
 * ========================================
 * Individual card components are imported from JourneyStepCards.jsx
 * Each card can be adjusted independently - NO LOOPS
 * See JourneyStepCards.jsx for all card components and positioning
 */

// ============ MAIN COMPONENT ============
const UserJourneyPage = () => {
  const theme = useTheme();
  // Breakpoint detection for responsive design
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md")); // Tablet: < 900px
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // Mobile: < 600px
  const isVerySmallMobile = useMediaQuery("(max-width:400px)"); // Very small mobile: < 400px
  const containerRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showMeeting, setShowMeeting] = useState(false);
  
  // Background SVG path - same as intro page (from public folder)
  const backgroundSvgPath = "/images/userJourney/assets/job297-ploy-07c-blue-01.svg";

  /**
   * ========================================
   * JOURNEY STEPS - Individual Components (NO LOOPS)
   * ========================================
   * 
   * All journey step cards are now individual components in JourneyStepCards.jsx
   * Each card can be adjusted independently:
   * - Open JourneyStepCards.jsx
   * - Find the card you want to adjust (JourneyStepCard1, 2, 3, 4, or 5)
   * - Modify the topDesktop, topTablet, topMobile values (in pixels)
   * - Modify the left/right percentages for horizontal positioning
   * 
   * All pixel values are clearly visible and easy to change!
   */

  /**
   * SCROLL ANIMATION TRACKING
   * 
   * This effect tracks scroll position and reveals the curve progressively.
   * - Desktop/Tablet: Starts at 15% visibility, progresses to 100% as user scrolls
   * - Mobile: No animation - curve is fully visible immediately (scrollProgress = 1)
   * - Uses clip-path CSS to create the reveal effect
   * - Uses requestAnimationFrame for smooth, throttled updates
   */
  useEffect(() => {
    // On mobile, show curve immediately without animation
    if (isMobile) {
      setScrollProgress(1); // Fully visible on mobile
      return;
    }

    let rafId = null;
    let ticking = false;

    // Desktop/Tablet: Scroll-based reveal animation with smooth throttling
    const handleScroll = () => {
      if (!ticking && containerRef.current) {
        rafId = requestAnimationFrame(() => {
          const container = containerRef.current;
          if (container) {
            const rect = container.getBoundingClientRect();
            const containerTop = rect.top + window.scrollY;
            const scrollTop = window.scrollY - containerTop;
            const containerHeight = container.scrollHeight - window.innerHeight;
            const rawProgress = Math.max(0, Math.min(1, scrollTop / containerHeight));
            
            // Start at 15% to show initial curve, then progress to 100%
            const progress = 0.15 + (rawProgress * 0.85);
            setScrollProgress(progress);
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Initial call
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [isMobile]);

  // ── Clarity Event Tracking ──────────────────────────────────────────────
  useEffect(() => {
    let smartReportCount = 0;

    const handleClick = (e) => {
      if (typeof window.clarity !== 'function') return;

      const btn = e.target.closest('button');
      if (!btn) return;
      const text = btn.innerText?.trim().toLowerCase() || '';

      // Step 1 & 2 — View Smart Report (differentiated by click-order counter)
      if (text.includes('view smart report')) {
        smartReportCount++;
        const stepName = smartReportCount === 1 ? 'branding_reporting' : 'retention_boost';
        window.clarity('set', 'uj_cta_clicked', `view_smart_report_${stepName}`);
        window.clarity('event', `uj_view_smart_report_${stepName}_clicked`);
      }

      // Step 3 — View Viz App
      if (text.includes('view viz app')) {
        window.clarity('set', 'uj_cta_clicked', 'view_viz_app');
        window.clarity('event', 'uj_view_viz_app_clicked');
      }

      // Step 4 — View Chat Bot
      if (text.includes('view chat bot')) {
        window.clarity('set', 'uj_cta_clicked', 'view_chat_bot');
        window.clarity('event', 'uj_view_chat_bot_clicked');
      }

      // Step 5 — View Analytics
      if (text.includes('view analytics')) {
        window.clarity('set', 'uj_cta_clicked', 'view_analytics');
        window.clarity('event', 'uj_view_analytics_clicked');
      }

      // Contact Now (NirogGyan Advantage section)
      if (text.includes('contact now')) {
        window.clarity('set', 'cta_clicked', 'contact_now');
        window.clarity('event', 'uj_contact_now_clicked');
      }
    };

    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
      smartReportCount = 0;
    };
  }, []);
  // ────────────────────────────────────────────────────────────────────────

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#ffffff",
        overflowX: "hidden",
        // Background SVG - same as intro page (from public folder)
        backgroundImage: `url(${backgroundSvgPath})`,
        backgroundSize: "cover",
        backgroundPosition: "center top",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "scroll",
      }}
    >
      {/* Content wrapper to ensure all sections appear above background SVG */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
        }}
      >
        {/* Hero Section */}
        <HeroSection isSmallScreen={isSmallScreen} isMobile={isMobile} />

        {/* 
          ========================================
          JOURNEY CONTENT CONTAINER
          ========================================
          This container holds the animated curve and all journey step cards.
          - No top padding to connect seamlessly with hero section
          - Responsive height based on device type
          - Transparent background to show SVG background
        */}
        <Box
          ref={containerRef}
          sx={{
            position: "relative",
            // Container height: Desktop=2900px, Tablet=2700px, Mobile=2200px
            minHeight: isMobile ? "1710px" : isSmallScreen ? "2430px" : "2490px",
            // Responsive padding: Desktop=40px 48px 120px, Tablet=30px 16px 80px, Mobile=20px 8px 60px
            padding: isMobile ? "20px 8px 60px" : isSmallScreen ? "30px 16px 80px" : "40px 48px 120px",
            paddingTop: 0, // Remove top padding to connect with hero section
            backgroundColor: "transparent", // Transparent to show background SVG
          }}
        >
        {/* 
          ========================================
          ANIMATED CURVE BACKGROUND - RESPONSIVE SIZING
          ========================================
          
          CURVE SIZE BREAKDOWN:
          - DESKTOP (≥900px): width="750px" - LARGE SIZE (bigger curve)
          - TABLET (600px-899px): width="85%" - MEDIUM SIZE (medium curve)
          - MOBILE (<600px): width="75%" - SMALL SIZE (small curve)
          
          To adjust curve size:
          - Desktop: Change "750px" to desired width (e.g., "800px" for bigger, "700px" for smaller)
          - Tablet: Change "85%" to desired percentage (e.g., "90%" for bigger, "80%" for smaller)
          - Mobile: Change "75%" to desired percentage (e.g., "80%" for bigger, "70%" for smaller)
          
          POSITIONING:
          - top: "-40px" pulls curve up to connect with hero section
          - left: "50%" centers the curve horizontally
          - transform: "translateX(-50%)" ensures perfect centering
          
          ANIMATION:
          - clipPath reveals curve from top to bottom as user scrolls
          - scrollProgress controls how much of curve is visible (0-100%)
        */}
        <Box
          sx={{
            position: "absolute",
            top: "-1px", // Pull curve up to connect with hero section
            left: "50%",
            transform: "translateX(-50%)",
            // RESPONSIVE CURVE WIDTH:
            // Desktop: 750px (LARGE - make it bigger)
            // Tablet: 85% of container (MEDIUM)
            // Mobile (400-850px): 90% of container
            // Very Small Mobile (<400px): 95% of container (wider to prevent overlap)
            width: isVerySmallMobile ? "95%" : isMobile ? "90%" : isSmallScreen ? "85%" : "750px",
            height: "100%",
            maxHeight: "3300px",
            zIndex: 1, // Behind cards, in front of background
            // Scroll reveal animation - reveals curve from top to bottom
            // Mobile: No animation (fully visible immediately)
            // Desktop/Tablet: Animated reveal based on scroll with smooth interpolation
            clipPath: `inset(0 0 ${100 - scrollProgress * 100}% 0)`,
            transition: isMobile ? "none" : "clip-path 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <Box
            component="img"
            src={newCurve}
            alt="User Journey Timeline"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "contain", // Maintains aspect ratio
              objectPosition: "top center", // Aligns to top center
            }}
          />
        </Box>

        {/* 
          ========================================
          JOURNEY STEPS RENDERING - INDIVIDUAL CARDS (NO LOOPS)
          ========================================
          Each card is rendered individually so you can adjust positions one by one.
          To adjust positions, open JourneyStepCards.jsx and modify the pixel values
          in each individual card component.
        */}
        {/* Step 1 Card - Left side */}
        <JourneyStepCard1 />
        
        {/* Step 2 Card - Right side */}
        <JourneyStepCard2 />
        
        {/* Step 3 Card - Left side */}
        <JourneyStepCard3 />
        
        {/* Step 4 Card - Right side */}
        <JourneyStepCard4 />
        
        {/* Step 5 Card - Left side */}
        <JourneyStepCard5 />

        {/* Advantage Section at Bottom - Redesigned */}
        <Box
          sx={{
            position: "absolute",
            bottom: isSmallScreen ? "40px" : "80px",
            left: "50%",
            transform: "translateX(-50%)",
            width: isSmallScreen ? "90%" : "85%",
            maxWidth: "1100px",
            zIndex: 2,
          }}
        >
          <Box
            sx={{
              background: "linear-gradient(135deg, #ffffff 0%, #f8faff 100%)",
              borderRadius: "24px",
              padding: isSmallScreen ? "28px" : "48px",
              boxShadow: "0 20px 60px rgba(2, 38, 116, 0.15), 0 0 0 1px rgba(60, 208, 255, 0.1)",
              border: "1px solid rgba(60, 208, 255, 0.2)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Decorative background elements */}
            <Box
              sx={{
                position: "absolute",
                top: "-50px",
                right: "-50px",
                width: "200px",
                height: "200px",
                borderRadius: "50%",
                background: "radial-gradient(circle, rgba(60, 208, 255, 0.1) 0%, transparent 70%)",
                pointerEvents: "none",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: "-30px",
                left: "-30px",
                width: "150px",
                height: "150px",
                borderRadius: "50%",
                background: "radial-gradient(circle, rgba(30, 77, 183, 0.08) 0%, transparent 70%)",
                pointerEvents: "none",
              }}
            />

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: isSmallScreen ? "24px" : "48px",
                flexDirection: isSmallScreen ? "column" : "row",
                position: "relative",
                zIndex: 1,
              }}
            >
              {/* Image Section */}
              <Box
                sx={{
                  flexShrink: 0,
                  position: "relative",
                }}
              >
                <Box
                  component="img"
                  alt="NirogGyan Advantage"
                  src={amicoAdvantage}
                  sx={{
                    width: isSmallScreen ? "140px" : "240px",
                    height: isSmallScreen ? "140px" : "240px",
                    objectFit: "contain",
                    filter: "drop-shadow(0 8px 24px rgba(60, 208, 255, 0.2))",
                  }}
                />
              </Box>

              {/* Content Section */}
              <Box sx={{ flex: 1 }}>
                {/* Title */}
                <Typography
                  sx={{
                    fontSize: isSmallScreen ? "22px" : "36px",
                    fontWeight: 800,
                    fontFamily: "'Poppins', sans-serif",
                    background: "linear-gradient(135deg, #022674 0%, #1E4DB7 50%, #3CD0FF 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    marginBottom: isSmallScreen ? "20px" : "28px",
                    lineHeight: 1.2,
                  }}
                >
                  The <strong>NirogGyan</strong> Advantage
                </Typography>

                {/* Benefits List */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: isSmallScreen ? "1fr" : "1fr 1fr",
                    gap: isSmallScreen ? "12px" : "16px",
                    marginBottom: isSmallScreen ? "24px" : "32px",
                  }}
                >
                  {[
                    "30% Increase in Patient Engagement with AI-driven insights",
                    "Higher Retention & Revenue Growth through structured follow-ups",
                    "Seamless Integration into any LIS/LIMS system",
                    "Reduced Support Costs with AI-powered patient guidance",
                  ].map((text, index) => (
                    <Box
                      key={text}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "12px",
                        padding: isSmallScreen ? "12px" : "16px",
                        borderRadius: "12px",
                        background: "linear-gradient(135deg, rgba(60, 208, 255, 0.05) 0%, rgba(30, 77, 183, 0.03) 100%)",
                        border: "1px solid rgba(60, 208, 255, 0.15)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          background: "linear-gradient(135deg, rgba(60, 208, 255, 0.1) 0%, rgba(30, 77, 183, 0.06) 100%)",
                          borderColor: "rgba(60, 208, 255, 0.3)",
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 12px rgba(60, 208, 255, 0.15)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: isSmallScreen ? "20px" : "24px",
                          height: isSmallScreen ? "20px" : "24px",
                          minWidth: isSmallScreen ? "20px" : "24px",
                          borderRadius: "50%",
                          background: "linear-gradient(135deg, #3CD0FF 0%, #1E4DB7 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          marginTop: "2px",
                          boxShadow: "0 2px 8px rgba(60, 208, 255, 0.4)",
                        }}
                      >
                        <Typography
                          sx={{
                            color: "white",
                            fontSize: isSmallScreen ? "12px" : "14px",
                            fontWeight: 700,
                            lineHeight: 1,
                          }}
                        >
                          ✓
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontSize: isSmallScreen ? "12px" : "15px",
                          lineHeight: 1.5,
                          color: "#1f2937",
                          fontWeight: 500,
                        }}
                      >
                        {text}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* CTA Section */}
                <Box
                  sx={{
                    padding: isSmallScreen ? "20px" : "28px",
                    borderRadius: "16px",
                    background: "linear-gradient(135deg, #022674 0%, #1E4DB7 50%, #3567D6 100%)",
                    border: "1px solid rgba(60, 208, 255, 0.3)",
                    boxShadow: "0 8px 24px rgba(2, 38, 116, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
                    textAlign: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: isSmallScreen ? "16px" : "22px",
                      fontWeight: 700,
                      fontFamily: "'Poppins', sans-serif",
                      color: "white",
                      marginBottom: isSmallScreen ? "16px" : "20px",
                      textShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
                    }}
                  >
                    Ready to Elevate Your Patient Experience?
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => setShowMeeting(true)}
                    sx={{
                      background: "transparent",
                      color: "white",
                      fontSize: isSmallScreen ? "14px" : "16px",
                      fontWeight: 700,
                      fontFamily: "'Poppins', sans-serif",
                      padding: isSmallScreen ? "10px 24px" : "12px 32px",
                      borderRadius: "12px",
                      border: "1px solid white",
                      borderColor: "white",
                      textTransform: "none",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                      },
                      "&:active": {
                        transform: "translateY(0)",
                      },
                    }}
                  >
                    Contact Now
                    <ArrowForwardIcon
                      sx={{
                        marginLeft: "8px",
                        fontSize: isSmallScreen ? "18px" : "20px",
                      }}
                    />
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      {/* End of content wrapper - ensures content appears above background SVG */}
      </Box>
      
      {/* Meeting Modal */}
      <MeetingModal showMeeting={showMeeting} setShowMeeting={setShowMeeting} />
    </Box>
  );
};

export default UserJourneyPage;
